import { policyType as PolicyType } from '@ourbranch/lookups';
import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useContext, useCallback } from 'react';

import { EntityDetailsFields } from 'common/components/entity-details';
import AddPerson from 'common/components/people/add-form';
import NonRatedIndividuals from 'common/components/people/non-rated-individuals';
import Suggestions from 'common/components/people/suggestions/suggestions';
import { BranchPlus } from 'common/components/branch-plus';
import { useDisabledState } from 'common/disabled-context';
import { AuthContext } from 'core/components/auth';
import FormList from 'core/components/form-list';
import { useToast } from 'core/components/toast';
import { capitalize } from 'core/helpers/formatters';
import { useStore } from 'core/store/store.mobx';
import { NON_RATED_INDIVIDUALS_SCROLL_TARGET } from 'common/components/people/non-rated-individuals/non-rated-individuals.constants';
import useStyles from './people-details.styles';
import { OfferApplicant, OfferDriver } from './person';

const PeopleDetails = observer(({ hasAuto }) => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const {
    values: { drivers, removedDrivers },
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const toast = useToast();
  const { disabled } = useDisabledState();

  const session = useContext(AuthContext);
  const isBranchPlusAvailable = session.canQuoteBranchPlus;
  const isApiOffer = store.isApiOffer;

  const checkForNonRatedIndividualsAndRemove = useCallback(
    (person) => {
      const firstName = person.firstName.trim().toUpperCase();
      const lastName = person.lastName.trim().toUpperCase();

      const personAlreadyAsNonRatedIndividual = removedDrivers.some(
        (driver) =>
          driver.firstName === firstName && driver.lastName === lastName && driver.dateOfBirth === person.dateOfBirth
      );
      if (personAlreadyAsNonRatedIndividual) {
        setFieldValue(
          'removedDrivers',
          removedDrivers.filter(
            (driver) =>
              driver.firstName !== firstName ||
              driver.lastName !== lastName ||
              driver.dateOfBirth !== person.dateOfBirth
          )
        );
        setFieldTouched('removedDrivers');
        toast.notify({
          type: 'success',
          message: `${capitalize(person.firstName)} ${capitalize(person.lastName)} was successfully added as a driver and removed as a non-rated individual.`
        });
      }
    },
    [removedDrivers, setFieldValue, setFieldTouched, toast]
  );

  return (
    <div className={classes.container}>
      <div id="entity">
        <EntityDetailsFields />
      </div>
      <FormList
        disabled={disabled}
        id="drivers" // this is confusing but no matter the policy type (e.g. home only) the people data will always be under the "drivers" node for offers
        item={hasAuto ? OfferDriver : OfferApplicant}
        addForm={(props) => (
          <AddPerson
            {...props}
            drivers={drivers}
            hasAuto={hasAuto}
            hideLicenseFields={store.isFarmOffer}
            addContent={<Suggestions isCoapplicant={!hasAuto} isDriver drivers={drivers} />}
          />
        )}
        addLabel={hasAuto ? 'Add Drivers' : 'Add Co-applicants'}
        title={hasAuto ? 'Drivers' : 'Applicants'}
        singular="person"
        plural="people"
        hasAuto={hasAuto}
        onAdd={checkForNonRatedIndividualsAndRemove}
      />
      {!store.isBranchPlusOnly && !store.isFarmOffer && (
        <NonRatedIndividuals openByDefault={isApiOffer || store.scrollTo === NON_RATED_INDIVIDUALS_SCROLL_TARGET} />
      )}
      {isBranchPlusAvailable && <BranchPlus policyType={PolicyType.Auto} />}
      {isBranchPlusAvailable && <BranchPlus policyType={PolicyType.Home} />}
    </div>
  );
});

export default PeopleDetails;

PeopleDetails.propTypes = {
  hasAuto: PropTypes.bool
};

PeopleDetails.defaultProps = {
  hasAuto: false
};
